import React, { ChangeEventHandler, ReactNode, Ref } from 'react';
import styled from 'styled-components';

import { colors, transition } from 'theme';
import { useUniqueId } from 'utils/hooks';

type Status = 'neutral' | 'success' | 'error';
type Presentation = 'default' | 'contrast' | 'light-border';

function highlightColorFor(status: Status, presentation: Presentation): string {
  if (status === 'success') return 'green';
  if (status === 'error') return 'red';

  if (presentation === 'light-border') return colors.aluminum;
  return colors.silver;
}

const Container = styled.div<{
  $status: Status;
  $presentation: Presentation;
  $fullHeight;
}>`
  ${({ $status, $presentation, $fullHeight }) => `
    --input-highlight-color: ${highlightColorFor($status, $presentation)};
    --input-affix-color: ${colors.middleGrey};
    --input-text-color: ${colors.charcoalGrey};
    --input-background-color: ${
      $presentation === 'contrast' ? colors.platinum : colors.white
    };
    --input-height: ${$fullHeight ? '100%' : '45px'};
  `}

  position: relative;
  overflow: hidden;
  display: flex;

  font-size: 18px;
  height: var(--input-height);
  color: var(--input-text-color);
  background: var(--input-background-color);

  border-radius: 4px;

  ${({ $presentation }) =>
    $presentation !== 'contrast' &&
    'border: solid 1px var(--input-highlight-color);'}

  transition: all ${transition};

  ${({ $status }) =>
    $status !== 'neutral' &&
    'box-shadow: 0 0 0 1px var(--input-highlight-color);'}

  &::placeholder {
    color: ${colors.middleGrey};
  }

  &:focus-within {
    border-color: ${colors.waterBlue};
    box-shadow: 0 0 0 1px ${colors.waterBlue};
    --input-affix-color: ${colors.waterBlue};
  }
`;

const Affix = styled.label`
  height: 100%;
  display: inline-flex;
  align-items: center;
  color: var(--input-affix-color);
  transition: all ${transition};
`;

// The input itself doesn't have many styles, most are applied to the container
const StyledInput = styled.input`
  padding: 11px 1em;
  flex: 1;

  appearance: none;
  box-shadow: none;
  border: none;
  outline: none !important;
  font: inherit;
  background: transparent;
`;

type Props = {
  inputProps: {
    id?: string;
    ref?: Ref<HTMLInputElement>;
    placeholder?: string;
    value: string;
    onChange?: ChangeEventHandler<HTMLInputElement>;
    disabled?: boolean;
    readOnly?: boolean;
    'data-qa-id'?: string;
    'aria-label'?: string;
  };
  prefix?: ReactNode;
  suffix?: ReactNode;
  status?: Status;
  presentation?: Presentation;
  fullHeight?: boolean;
};

export function Input({
  inputProps: {
    id,
    ref,
    placeholder,
    value,
    onChange,
    disabled,
    readOnly,
    'data-qa-id': dataQaId,
    'aria-label': ariaLabel,
  },
  prefix,
  suffix,
  status = 'neutral',
  presentation = 'default',
  fullHeight,
}: Props) {
  const inputId = useUniqueId({ prefix: 'input-group-', override: id });

  return (
    <Container
      $status={status}
      $presentation={presentation}
      $fullHeight={fullHeight}
    >
      {prefix && <Affix htmlFor={inputId}>{prefix}</Affix>}

      <StyledInput
        id={inputId}
        ref={ref}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={disabled}
        readOnly={readOnly}
        data-qa-id={dataQaId}
        aria-label={ariaLabel}
      />

      {suffix && <Affix htmlFor={inputId}>{suffix}</Affix>}
    </Container>
  );
}
